import { ActivatedRoute, ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { Injectable } from '@angular/core';

import { NotificationService } from '@xpo-ltl/data-api';
import {
  CustomerApiService,
  DetermineRestrictedBillToResp,
  DetermineRestrictedBillToRqst,
  GetCustomerGroupDetailsResp,
  GetCustomerGroupDetailsQuery,
} from '@xpo-ltl/sdk-customer';
import { XrtCustomerSearchFilter, XrtCustomerSearchRqst, XrtSearchQueryHeader } from '@xpo-ltl/sdk-elasticsearch';

import {
  CustomerApiService as CustomerApiService2,
  CustomerMatch,
  MatchCustomerByMadCodePath,
  MatchCustomerByMadCodeQuery,
  MatchCustomerByMadCodeResp,
} from '@xpo-ltl-2.0/sdk-customer';

import { BehaviorSubject, throwError } from 'rxjs';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, take } from 'rxjs/operators';

import { Customer } from '../model/customer';
import { CustomerGroup } from '../model/customer-group';
import { CustomerRuleService } from '../services/customer-rule/customer-rule.service';
import { CustomerSearchTransformer } from '../components/customer-search/customer-search-transformer';
import { RouterParams, RouterUriComponents } from '../enums/router-uri-components.enum';
import { CustomerSearchMadCdForApiModel, CustomerSearchTypeModel } from '../model/customer-search';
import { CustomerFunctionCd, CustomerTypeCd, Envelope, ListInfo } from '@xpo-ltl/sdk-common';

@Injectable()
export class SearchKeyResolver implements Resolve<boolean> {
  private xrtCustomerSearchRqst: XrtCustomerSearchRqst;
  private customerInfoSubject = new BehaviorSubject<Customer>(undefined);
  private customerGroupInfoSubject = new BehaviorSubject<CustomerGroup>(undefined);
  private searchErrorMessageSubject = new BehaviorSubject<string>(undefined);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private customerApiService: CustomerApiService,
    private customerRuleService: CustomerRuleService,
    private notificationService: NotificationService,
    private customerApiService2: CustomerApiService2
  ) {
    this.xrtCustomerSearchRqst = new XrtCustomerSearchRqst();
    this.xrtCustomerSearchRqst.header = new XrtSearchQueryHeader();
    this.xrtCustomerSearchRqst.filter = new XrtCustomerSearchFilter();
    this.xrtCustomerSearchRqst.header.page = 1;
    this.xrtCustomerSearchRqst.header.pageSize = 1;
    // SPEED-2519 - Only interest in real customer address and not Alias.
    // customer with seqNbr = 0 is the real customer, and sorting ascending will put seqNbr ZERO on top.
    this.xrtCustomerSearchRqst.header.sortExpressions = [
      { IsDescendingSort: true, Column: 'seqNbr', direction: 'asc' },
    ];
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // console.log('route key = ' + this.routeId_param);
    const searchValue = route.params[RouterParams.SEARCH_KEY];
    if (searchValue) {
      this.search(searchValue);
      return of(true);
    } else {
      return of(false);
    }
  }

  private search(searchValue: any) {
    if (this.isNumeric(searchValue)) {
      this.searchCustomerByGroupId(searchValue);
    } else {
      this.xrtCustomerSearch(searchValue);
    }
  }

  private isNumeric(value) {
    return value != null && !isNaN(value);
  }

  private searchCustomerByGroupId(searchValue: any) {
    const customerGroupDetailsRqst = new GetCustomerGroupDetailsQuery();
    customerGroupDetailsRqst.custGroupInstId = +searchValue;

    this.customerApiService
      .getCustomerGroupDetails(customerGroupDetailsRqst)
      .pipe(take(1))
      .subscribe(
        (customerGroupDetailsResp: GetCustomerGroupDetailsResp) => {
          const customerGroup = CustomerSearchTransformer.toCustomerGroup(customerGroupDetailsResp.custGroup);
          this.customerGroupInfoSubject.next(customerGroup);
          this.customerRuleService.setCustomerGroupDetails(customerGroup);
          if (this.router.url && !this.router.url.endsWith(RouterUriComponents.CUSTOMER_RULE)) {
            this.router.navigate([RouterUriComponents.CUSTOMER_RULE], { skipLocationChange: true });
          }
        },
        (error) => {
          this.searchErrorMessageSubject.next(error['error'] ? error['error']['message'] || '' : undefined);
        }
      );
  }

  getCustomerTypes(requestCustomerTypes: CustomerSearchTypeModel): (CustomerTypeCd | CustomerFunctionCd)[] {
    const customerTypes = [];
    customerTypes.push(requestCustomerTypes.pickupDelivery);
    customerTypes.push(requestCustomerTypes.billTo);
    customerTypes.push(requestCustomerTypes.corporate);
    return customerTypes;
  }

  getCustomerTypesForMadCode(): CustomerFunctionCd[] {
    const requestCustomerTypes: CustomerSearchTypeModel = {
      pickupDelivery: CustomerFunctionCd.PICKUP_OR_DELIVERY,
      billTo: CustomerFunctionCd.BILL_TO,
      corporate: CustomerFunctionCd.CORPORATE,
    };

    return this.getCustomerTypes(requestCustomerTypes) as CustomerFunctionCd[];
  }

  getMadCodeRequest(madCode: string): CustomerSearchMadCdForApiModel {
    return {
      madCd: madCode,
      customerTypes: this.getCustomerTypesForMadCode(),
    };
  }

  getMatchCustomerByMadCode(customerData: CustomerSearchMadCdForApiModel): Observable<MatchCustomerByMadCodeResp> {
    const pathParams = new MatchCustomerByMadCodePath();
    const queryParams = new MatchCustomerByMadCodeQuery();
    pathParams.madCode = customerData.madCd;
    queryParams.customerFunctionCd = customerData.customerTypes;
    queryParams.listInfo = {
      numberOfRows: 1,
      startAt: 1,
    } as ListInfo;

    return this.customerApiService2.matchCustomerByMadCode(pathParams, queryParams).pipe(
      catchError((response: Envelope<MatchCustomerByMadCodeResp>) => {
        console.error(response);
        return throwError(response);
      })
    );
  }

  private xrtCustomerSearch(searchValue: any) {
    const madCode = this.getMadCodeRequest(searchValue);

    this.getMatchCustomerByMadCode(madCode)
      .pipe(
        take(1),
        mergeMap((matchCustomerSearchResp: MatchCustomerByMadCodeResp) => {
          const response = { restricted: false, customer: matchCustomerSearchResp };

          const customer = matchCustomerSearchResp
            ? matchCustomerSearchResp.customerMatch.find(
                (item: CustomerMatch) => item.customerTypeCd === CustomerTypeCd.BILL_TO
              )
            : undefined;
          if (customer) {
            const request = new DetermineRestrictedBillToRqst();
            request.billToCustNbr = +customer.customerNbr;
            return this.customerApiService.determineRestrictedBillTo(request).pipe(
              take(1),
              map((determineRestrictedBillToResp: DetermineRestrictedBillToResp) => {
                if (response) {
                  response.restricted = determineRestrictedBillToResp.billToIsRestrictedInd;
                }

                return response;
              })
            );
          }
          return of(response);
        })
      )
      .subscribe((response: { restricted: boolean; customer: MatchCustomerByMadCodeResp }) => {
        const matchCustomerSearchResp = response.customer;

        if (matchCustomerSearchResp.customerMatch.length > 0) {
          // Found customer
          const customer = CustomerSearchTransformer.toCustomer(
            matchCustomerSearchResp.customerMatch[0],
            response.restricted
          );
          this.customerInfoSubject.next(customer);
          this.customerRuleService.setCustomerDetails(customer);
          if (this.router.url && !this.router.url.endsWith(RouterUriComponents.CUSTOMER_RULE)) {
            this.router.navigate([RouterUriComponents.CUSTOMER_RULE], { skipLocationChange: true });
          }
        } else {
          // Customer not found.
          this.notificationService.showSnackBarMessage('No Customer Found', {
            durationInMillis: 5000,
            status: 'error',
          });
          this.searchErrorMessageSubject.next('No Customer Found');
        }
      });
  }
}
